import PropTypes from 'prop-types';
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Grid,
  useMediaQuery,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { styled } from '@mui/material/styles';
import { useEffect, useState, useMemo } from 'react';
import Iconify from '../../../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../../../reusable-components/snackbar';
import { getOrdersForTicket } from '../../../../../../api/tickets';
import { getPendingOrderInfoData } from '../../../../../../api/orders';
import CustomDataGrid from '../../../../../../reusable-components/datagrid/CustomDataGrid';
import { PENDING_ORDER } from '../../../../../../reusable-components/datagrid/pendingOrderColumns';
import { RENTAL_ORDER_ITEMS } from '../../../../../../reusable-components/datagrid/rentalColumns';
import { useSelector } from '../../../../../../redux/store';
import { getItemsByOrderId } from '../../../../../../api/rentals';
import CloseIconButton from '../../../../../../reusable-components/basic-buttons/CloseIconButton';

TicketOrderSearch.propTypes = {
  facilityId: PropTypes.number,
  onChange: PropTypes.func,
  sx: PropTypes.object,
  inputVariant: PropTypes.string,
  selectedOrderId: PropTypes.number,
};

export default function TicketOrderSearch({
  facilityId,
  onChange,
  sx = {},
  inputVariant = 'outlined',
  selectedOrderId = null,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [orders, setOrders] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [previewOrder, setPreviewOrder] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const isLoading = useSelector((state) => state.orders.isLoading);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [mobileFilteredOrders, setMobileFilteredOrders] = useState([]);
  const [mobileOrderSearch, setMobileOrderSearch] = useState('');

  const formatOrderOptions = (ordersList) =>
    ordersList
      .slice()
      .sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded))
      .map((order) => ({
        id: order.id,
        label: `Order# ${order.orderNumber}`,
        orderNumber: order.orderNumber,
        itemsCount: order.itemsCount,
        department: order.department || 'N/A',
        submittedBy: order.submittedBy || 'N/A',
        dateAdded: order.dateAdded || 'N/A',
        onBehalfOf: order.personFor || 'N/A',
        approvedBy: order.approvedBy || 'N/A',
        orderType: order.orderType || 'N/A',
      }));

  const filteredOrderOptions = useMemo(() => formatOrderOptions(mobileFilteredOrders), [mobileFilteredOrders]);
  const allOrderOptions = useMemo(
    () =>
      (orders || [])
        .slice()
        .sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded))
        .map((order) => ({
          id: order.id,
          label: `Order# ${order.orderNumber}`,
          orderNumber: order.orderNumber,
          itemsCount: order.itemsCount,
          department: order.department || 'N/A',
          submittedBy: order.submittedBy || 'N/A',
          dateAdded: order.dateAdded || 'N/A',
          onBehalfOf: order.personFor || 'N/A',
          approvedBy: order.approvedBy || 'N/A',
          orderType: order.orderType || 'N/A',
        })),
    [orders]
  );

  useEffect(() => {
    setMobileFilteredOrders(
      orders?.filter((order) => order.orderNumber.includes(mobileOrderSearch)).slice(0, 50) ?? []
    );
  }, [orders, mobileOrderSearch]);

  useEffect(() => {
    if (selectedOrderId && !selectedOrder && allOrderOptions) {
      const preselected = allOrderOptions.find((order) => order.id === selectedOrderId);
      if (preselected) setSelectedOrder(preselected);
    }
  }, [selectedOrderId, allOrderOptions]);

  useEffect(() => {
    setOrders(null);
    setSelectedOrder(null);
    setMobileOrderSearch('');
    if (facilityId > 0) {
      getOrdersForTicket(facilityId)
        .then((res) => setOrders(res.data))
        .catch(() => {
          enqueueSnackbar('Failed to get orders', { variant: 'error' });
          setOrders([]);
        });
    }
  }, [facilityId, enqueueSnackbar]);

  const handlePreviewOrder = async (order) => {
    setPreviewOrder(order);
    setPreviewData([]);
    setPreviewDialogOpen(true);
    try {
      if(order.orderType === 'Rental'){
        const rentalDetails = await getItemsByOrderId(order.id);
        const rentalItems = rentalDetails?.data?.map((item) => ({
          department: item.categoryName,
          subcategory: item.subcategoryName,
          vendor: item.vendorName,
          ...item
        }));
        setPreviewData(rentalItems || []);
      }
      else {
        const details = await getPendingOrderInfoData(order.id);
        setPreviewData(details.data.items || []);
      }
    } catch {
      setPreviewData([]);
    }
  };
  const infoStyle = {
    fontSize: '0.78rem',
    wordBreak: 'break-word',
  };

  return (
    <>
      {isMobile ? (
        <Autocomplete
          size="small"
          fullWidth
          sx={{ mb: 2, ...sx }}
          options={filteredOrderOptions}
          value={selectedOrder}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option?.label || `Order# ${option?.orderNumber ?? ''}`
          }
          renderInput={(params) => (
            <TextField variant="standard" {...params} placeholder="Start typing your order #" autoComplete="off" />
          )}
          onChange={(event, newValue) => {setSelectedOrder(newValue); onChange(newValue) }}
          onInputChange={(event, newValue) => setMobileOrderSearch(newValue?.replace('Order# ', ''))}
          loading={orders === null}
          renderOption={(props, option) => (
            <li {...props}>
              <OptionWrapper>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                  <strong>{option.label}</strong>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviewOrder(option);
                    }}
                    aria-label="Preview Order"
                  >
                    <Iconify icon="mdi:eye" width={16} height={16} />
                  </IconButton>
                </div>
                <span style={infoStyle}>Department: {` ${option.department}`}</span>
                <span style={infoStyle}>Items: {` ${option.itemsCount}`}</span>
                <span style={infoStyle}>Date Added: {new Date(option.dateAdded).toLocaleDateString()}</span>
                <span style={infoStyle}>Submitted By: {` ${option.submittedBy?.id}`}</span>
                <span style={infoStyle}>On Behalf Of: {` ${option.onBehalfOf}`}</span>
                <span style={infoStyle}>Approved By: {` ${option.approvedBy}`}</span>
              </OptionWrapper>
            </li>
          )}
        />
      ) : (
        <TextField
          size="small"
          fullWidth
          sx={{ minWidth: 200, ...sx }}
          value={selectedOrder ? `Order# ${selectedOrder.orderNumber}` : selectedOrderId ?  `Order# ${selectedOrderId}`: '' }
          placeholder="Attach an order"
          variant={inputVariant}
          onClick={() => setSelectionDialogOpen(true)}
          InputProps={{
            readOnly: true,
            endAdornment: selectedOrder && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOrder(null);
                  onChange(null);
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
      )}

      <Dialog
        open={selectionDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setSelectionDialogOpen(false);
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <CloseIconButton onClick={() => setSelectionDialogOpen(false)}/>
          Select an Order
        </DialogTitle>
        <DialogContent>
          <CustomDataGrid
            gridId="ticket-order-select"
            getRowId={(row) => row.orderType === 'Rental' ? `rental_${row.id}` : `order_${row.id}`}
            data={allOrderOptions}
            gridColumns={[
              { field: 'orderNumber', headerName: 'Order #', flex: 1 },
              { field: 'department', headerName: 'Department', flex: 1 },
              { field: 'itemsCount', headerName: 'Items', width: 100 },
              {
                field: 'dateAdded',
                headerName: 'Date Added',
                flex: 1,
                valueFormatter: ({ value }) => new Date(value).toLocaleDateString(),
              },
              {
                field: 'actions',
                headerName: 'Preview',
                type: 'actions',
                getActions: (params) => [
                  <Tooltip title="Preview" key={`preview-${params.row.id}`}>
                    <IconButton
                      key={`preview-${params.row.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewOrder(params.row);
                      }}
                    >
                      <RemoveRedEyeIcon width={20} height={20} />
                    </IconButton>
                  </Tooltip>,
                ],
              },
            ]}
            onRowClick={(params) => {
              setSelectedOrder(params.row);
              onChange(params.row);
              setSelectionDialogOpen(false);
            }}
            isLoading={orders === null || isLoading}
            boxSX={{ height: '70vh' }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={previewDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setPreviewDialogOpen(false);
        }}
        maxWidth="xl"
        fullWidth
      >
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item sx={{ mt: 2 }}>
            <Button
              variant="text"
              onClick={() => {
                setPreviewDialogOpen(false);
                setPreviewOrder(null);
                setPreviewData([]);
              }}
              startIcon={<Iconify icon="mingcute:left-fill" width={24} height={24} />}
              sx={{ color: '#454F5B', fontWeight: 'bold' }}
            >
              Back to Order Selection
            </Button>
            <CloseIconButton onClick={() => setPreviewDialogOpen(false)}/>
          </Grid>
        </Grid>

        <DialogTitle sx={{ textAlign: 'center' }}>Order Preview - {previewOrder?.orderNumber}</DialogTitle>
        <DialogContent>
          <CustomDataGrid
            gridId="orders-pending-order-preview"
            data={previewData}
            getRowId={(row) => row.id || row.itemId}
            gridColumns={previewOrder?.orderType === 'Rental' ? [...RENTAL_ORDER_ITEMS] : [...PENDING_ORDER]}
            getRowClassName={(params) => (params.row.deleted === true ? 'disabled' : '')}
            isLoading={previewData.length === 0 || isLoading}
            boxSX={{ height: '60vh' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
const OptionWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.0),
  backgroundColor: theme.palette.background.paper,
  fontSize: '0.85rem',
  '& span, & strong': {
    fontSize: '0.85rem',
  },
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  borderBottom: `1px solid #000`,
  borderTop: `1px solid #000`,
  padding: theme.spacing(1),
  width: '100%',
}));
